// [PURPOSE]: Combat-toggle flag definitions for the QuickActionsBar UI.
// [WHY]: Phase 4b registered many conditional effects against context flags
//   read from playerStats._activeFlags.<name> (via _buildContext). The data
//   side fires correctly when a flag is set, but no UI was flipping the
//   flags. This file is the single source of truth for which flags are
//   exposed in the Combat Toggles section, when they're relevant, and how
//   they're displayed.
// [INTEGRATION]: QuickActionsBar imports getActiveCombatToggles + the toggle
//   list. Player flips a flag → setPlayerStats updates _activeFlags → next
//   calc through effectCalculator picks up the new value via _buildContext.
// [Phase 4b combat-panel UI — APR 2026]: Closes BC-2 from the Bucket 3 spec.
// [PHASE B / #022 — 2026-05-06]: Class-only `isRelevant` predicates
//   migrated to read `combatToggles` arrays declared on SRD_CLASSES via
//   `classOwnsCombatToggleFlag`. The 9 remaining toggles use richer gating
//   (subclass names, multi-subclass lists, state-gated subclassData reads,
//   knownSpells layered checks) that needs a richer schema; they remain
//   in custom predicates pending the Bucket 1 architectural pass.
import { classOwnsCombatToggleFlag } from '../utils/combatToggleOwnership';
// [PHASE B / #035 — 2026-05-06]: Property-detection scan for the
//   `surfacesCombatToggle` data-block field. Replaces the 3 layered /
//   state-gated / multi-subclass string-matching predicates with a
//   single uniform helper that walks owned data (known/prepared
//   spells, active subclass-option picks, resolved class+subclass
//   features) for declarations matching the toggle's flag.
import { surfacesCombatToggleFlag } from '../utils/combatToggleSurfacing';

/**
 * Each toggle:
 *   flag:         string  — key in playerStats._activeFlags
 *   name:         string  — displayed label
 *   icon:         string  — emoji icon for visual identification
 *   description:  string  — short tooltip / explainer
 *   isRelevant:   (playerStats) => boolean — whether to show this toggle
 *                                            for the current character
 *
 * Relevance keeps the UI tight: a Rogue sees Sneak Attack but not Hexblade
 * Curse; a Hexblade sees Curse but not Vow of Enmity. The list never grows
 * past what's actually mechanically meaningful for the player.
 */
export const COMBAT_TOGGLES = [
  {
    flag: 'sneakAttackActive',
    name: 'Sneak Attack',
    icon: '🗡️', // 🗡️
    description: 'Once per turn: extra dice on a finesse or ranged hit with advantage OR ally adjacent. Toggle ON before rolling damage.',
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'sneakAttackActive'),
  },
  {
    flag: 'criticalHit',
    name: 'Apply Crit-Conditional Effects',
    icon: '💥', // 💥
    description: "Toggle ON after you roll a natural crit, BEFORE rolling damage. This does NOT force a crit — it tells the calculator to apply Brutal Critical extra dice (Barbarian L9+), Hexblade's Curse crit dice, and other crit-only effects to the upcoming damage roll.",
    isRelevant: () => true, // any character can crit
  },
  {
    flag: 'recklessAttackActive',
    name: 'Reckless Attack',
    icon: '🐯', // 🐯
    description: "Barbarian L2: advantage on STR melee attacks this turn — but enemies have advantage attacking you back.",
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'recklessAttackActive'),
  },
  {
    flag: 'huntersMarkTargetActive',
    name: "Hunter's Mark target",
    icon: '🎯', // 🎯
    description: "Currently attacking a creature you've Hunter's Mark'd. Adds 1d6 weapon damage on hit.",
    // [PHASE B / #035 — 2026-05-06]: Pre-fix this predicate string-
    //   matched `s.name === "Hunter's Mark"` against known/prepared
    //   spells AND name-matched `className === 'Ranger'` for the
    //   class-spell-list fallback. Now both checks reduce to a
    //   property scan: any spell in known/prepared with
    //   `surfacesCombatToggle: 'huntersMarkTargetActive'` (and the
    //   Hunter's Mark spell entry in srdSpells.js declares it). The
    //   "Ranger sees the toggle even without prep" surface goes via
    //   `classOwnsCombatToggleFlag` against
    //   SRD_CLASSES.Ranger.combatToggles (declared in #022).
    isRelevant: (playerStats) =>
      surfacesCombatToggleFlag(playerStats, 'huntersMarkTargetActive')
      || classOwnsCombatToggleFlag(playerStats, 'huntersMarkTargetActive'),
  },
  {
    flag: 'attackingHexbladeCursedTarget',
    name: "Hexblade's Curse target",
    icon: '💀', // 💀
    description: "Currently attacking a creature you've cursed via Hexblade's Curse. +prof bonus damage; crit on 19-20.",
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'attackingHexbladeCursedTarget'),
  },
  {
    flag: 'attackingSurprisedTarget',
    name: 'Attacking surprised target',
    icon: '😲', // 😲
    description: 'Target was surprised at start of combat. Assassin Assassinate: any hit is a critical hit.',
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'attackingSurprisedTarget'),
  },
  {
    flag: 'targetBelowFullHp',
    name: 'Target below full HP',
    icon: '🩸', // 🩸
    description: "Hunter's Colossus Slayer: +1d8 damage to a creature below full HP, once per turn.",
    // [PHASE B / #035 — 2026-05-06]: Pre-fix string-matched
    //   `className === 'Ranger' && subclass === 'Hunter' &&
    //   subclassData.hunterOptions.3 === 'Colossus Slayer'`. Now
    //   the Colossus Slayer choice in srdSubclassFeatures.js
    //   declares `surfacesCombatToggle: 'targetBelowFullHp'`; the
    //   helper resolves the chosen option via the choice block's
    //   `choiceKey: 'hunterOptions.3'` path and reads its field.
    isRelevant: (playerStats) =>
      surfacesCombatToggleFlag(playerStats, 'targetBelowFullHp'),
  },
  {
    flag: 'attackingFavoredEnemy',
    name: 'Favored Enemy target',
    icon: '🏹', // 🏹
    description: 'Currently attacking a creature type listed as your Favored Enemy. Surfaces Ranger L20 Foe Slayer +WIS mod attack/damage.',
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'attackingFavoredEnemy'),
  },
  {
    flag: 'attackingVowedTarget',
    name: 'Vowed target',
    icon: '⚔️', // ⚔️
    description: 'Vow of Enmity active vs this target (Paladin Oath of Vengeance). Advantage on attack rolls vs vowed target.',
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'attackingVowedTarget'),
  },
  {
    flag: 'castingClericCantripDamage',
    name: 'Casting Cleric cantrip damage',
    icon: '✨', // ✨
    description: 'Currently rolling damage on a cleric cantrip. Activates Potent Spellcasting +WIS mod (Knowledge / Light / Grave / Peace L8+).',
    // [PHASE B / #035 — 2026-05-06]: Pre-fix string-matched
    //   `className === 'Cleric' && level >= 8 && subclass IN
    //   ['Knowledge Domain', 'Light Domain', 'Grave Domain',
    //   'Peace Domain']`. Now the Potent Spellcasting feature on each
    //   of those 4 Domain subclasses declares
    //   `surfacesCombatToggle: 'castingClericCantripDamage'`; the
    //   helper resolves the player's active subclass features at
    //   their current level and finds the field. minLevel: 8 on the
    //   feature gates the L8+ requirement.
    isRelevant: (playerStats) =>
      surfacesCombatToggleFlag(playerStats, 'castingClericCantripDamage'),
  },
  {
    flag: 'castingDraconicElementSpell',
    name: 'Casting draconic-element spell',
    icon: '🐉', // 🐉
    description: 'Currently rolling damage on a spell matching your Dragon Ancestor element. Activates Elemental Affinity +CHA damage.',
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'castingDraconicElementSpell'),
  },
  {
    flag: 'castingEvocationSpellDamage',
    name: 'Casting Evocation damage',
    icon: '🌟', // 🌟
    description: 'Currently rolling damage on a wizard evocation spell. Activates Empowered Evocation +INT mod on one damage roll per cast.',
    isRelevant: (playerStats) => classOwnsCombatToggleFlag(playerStats, 'castingEvocationSpellDamage'),
  },
];

Object.freeze(COMBAT_TOGGLES);
COMBAT_TOGGLES.forEach(t => Object.freeze(t));

/**
 * Returns the subset of COMBAT_TOGGLES relevant for the given character.
 * If isRelevant throws (defensive — e.g., malformed playerStats), the toggle
 * is dropped silently rather than crashing the UI.
 *
 * @param {Object} playerStats
 * @returns {Array<{flag, name, icon, description}>}
 */
export function getActiveCombatToggles(playerStats) {
  if (!playerStats) return [];
  return COMBAT_TOGGLES.filter(toggle => {
    try {
      return toggle.isRelevant(playerStats);
    } catch (err) {
      console.warn('[combatToggles] isRelevant threw for flag:', toggle.flag, err);
      return false;
    }
  });
}

/**
 * Helper for a setPlayerStats setter to flip a single flag.
 * Returns a new playerStats object with _activeFlags[flag] = nextValue.
 *
 * @param {Object} playerStats - current playerStats
 * @param {string} flag - flag name to toggle (e.g., 'sneakAttackActive')
 * @param {boolean} [nextValue] - explicit next value; if omitted, toggles current
 * @returns {Object} new playerStats object
 */
export function setActiveFlag(playerStats, flag, nextValue) {
  if (!playerStats || !flag) return playerStats;
  const currentFlags = playerStats._activeFlags || {};
  const targetValue = typeof nextValue === 'boolean'
    ? nextValue
    : !currentFlags[flag];
  return {
    ...playerStats,
    _activeFlags: {
      ...currentFlags,
      [flag]: targetValue,
    },
  };
}

/**
 * Read the current value of a combat toggle flag. Returns false when the
 * playerStats has no _activeFlags or the flag is unset.
 */
export function isActiveFlag(playerStats, flag) {
  return !!(playerStats?._activeFlags?.[flag]);
}

export default COMBAT_TOGGLES;
